import { default as React } from 'react';
import { useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * Extracts a Kubernetes-compliant application name from a GitHub repository URL.
 *
 * IMPORTANT: This mirrors the backend logic in:
 * plugins/scaffolder-backend-module-quick-projects/src/utils/github.ts -> extractAppName()
 *
 * If you add a new GitHub organization or change an org alias, you MUST update BOTH files.
 * The orgMapping below must stay in sync with the backend version.
 */
function extractAppNameFromUrl(githubRepoUrl: string): string | null {
  // Supports root URLs and monorepo subfolder URLs (both /tree/ and /blob/):
  //   https://github.com/org/repo
  //   https://github.com/org/repo/tree/branch/apps/service-a
  //   https://github.com/org/repo/blob/branch/apps/service-a
  const match = githubRepoUrl
    .trim()
    .match(
      /github\.com[/:]([^/]+)\/([^/]+?)(?:\.git)?(?:\/(?:tree|blob)\/[^/]+\/(.+))?$/,
    );

  if (!match) {
    return null;
  }

  const orgMapping: Record<string, string> = {
    'wsky-test': 'wst',
    mediwareinc: 'minc',
  };

  const originalOrg = match[1].toLowerCase();
  const mappedOrg = orgMapping[originalOrg] || originalOrg;

  // For monorepo subfolder URLs use the subfolder leaf (mirrors backend extractAppName).
  let appPart: string;
  if (match[3]) {
    const subfolderParts = match[3].split('/').filter(Boolean);
    appPart = subfolderParts[subfolderParts.length - 1].toLowerCase();
  } else {
    appPart = match[2].toLowerCase();
  }

  const name = `${appPart}-${mappedOrg}`
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-')
    .slice(0, 63);

  if (!/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(name)) {
    return null;
  }

  return name;
}

// GCP Workload Identity Pool configuration — internal microapp cluster
// Dev: plateng-dev-app-wsky (project 880599546964)
// Prod: plateng-prod-app-wsky (project 791409896686)
const WORKLOAD_IDENTITY_POOLS = {
  dev: 'principal://iam.googleapis.com/projects/880599546964/locations/global/workloadIdentityPools/plateng-dev-app-wsky.svc.id.goog',
  prod: 'principal://iam.googleapis.com/projects/791409896686/locations/global/workloadIdentityPools/plateng-prod-app-wsky.svc.id.goog',
};

// GCP Workload Identity Pool configuration — public microapp cluster
// Dev: plateng-dev-apppub-wsky (project 427690665684)
// Prod: plateng-prod-apppub-wsky (project 408294115186)
const PUBLIC_WORKLOAD_IDENTITY_POOLS = {
  dev: 'principal://iam.googleapis.com/projects/427690665684/locations/global/workloadIdentityPools/plateng-dev-apppub-wsky.svc.id.goog',
  prod: 'principal://iam.googleapis.com/projects/408294115186/locations/global/workloadIdentityPools/plateng-prod-apppub-wsky.svc.id.goog',
};

/**
 * Extracts app name for public app hosting — no org suffix.
 * Mirrors extractPublicAppName() in plugins/scaffolder-backend-module-quick-projects/src/utils/github.ts.
 */
function extractPublicAppNameFromUrl(githubRepoUrl: string): string | null {
  const match = githubRepoUrl
    .trim()
    .match(
      /github\.com[/:]([^/]+)\/([^/]+?)(?:\.git)?(?:\/(?:tree|blob)\/[^/]+\/(.+))?$/,
    );
  if (!match) return null;

  let appPart: string;
  if (match[3]) {
    const subfolderParts = match[3].split('/').filter(Boolean);
    if (subfolderParts.length === 0) return null;
    appPart = subfolderParts[subfolderParts.length - 1].toLowerCase();
  } else {
    appPart = match[2].toLowerCase();
  }

  const name = appPart
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-')
    .slice(0, 63);

  return /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(name) ? name : null;
}

/**
 * Read-only info field that displays the computed GCP Workload Identity principal
 * for the microapp based on the GitHub repository URL.
 *
 * The principal follows the pattern:
 * principal://iam.googleapis.com/projects/791409896686/locations/global/workloadIdentityPools/plateng-prod-app-wsky.svc.id.goog/subject/ns/{namespace}/sa/{service_account}
 *
 * Where:
 * - namespace = qp-{appName}
 * - service_account = qp-{appName}-sa
 *
 * This component is always displayed in the Microapp Hosting scaffolder form
 * (not gated by secrets) because every microapp gets a Kubernetes service account
 * for Workload Identity, enabling GCP resource access (Secret Manager, Cloud SQL,
 * BigQuery, etc.) via IAM bindings.
 */
export const ServiceAccountInfo = ({
  formContext,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const [copied, setCopied] = React.useState(false);

  // public-app-hosting.yaml sets ui:options.isPublicApp = true.
  // Internal microapp-hosting.yaml leaves it unset.
  const isPublicApp =
    (uiSchema?.['ui:options']?.isPublicApp as boolean) ?? false;

  // Detect environment from the browser's current hostname, not app config.
  // - localhost, 127.0.0.1, *.dev.wellsky.cloud → dev WIF pool
  // - skystage.wellsky.cloud (no .dev) → prod WIF pool
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const isDevEnvironment =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('.dev.wellsky.cloud');

  const wifPools = isPublicApp
    ? PUBLIC_WORKLOAD_IDENTITY_POOLS
    : WORKLOAD_IDENTITY_POOLS;
  const workloadIdentityPool = isDevEnvironment ? wifPools.dev : wifPools.prod;

  // Get the GitHub repo URL from form context
  const githubRepoUrl = useMemo(() => {
    const repoUrlField = formContext?.formData?.githubRepoUrl;
    if (typeof repoUrlField === 'string') {
      return repoUrlField;
    }
    return repoUrlField?.value || '';
  }, [formContext?.formData?.githubRepoUrl]);

  // Check if staging environment is enabled (internal apps only)
  const enableStagingEnvironment = useMemo(() => {
    return formContext?.formData?.enableStagingEnvironment === true;
  }, [formContext?.formData?.enableStagingEnvironment]);

  // Derive service account and principal from the GitHub URL
  const serviceAccountInfo = useMemo(() => {
    if (!githubRepoUrl) {
      return null;
    }

    const appName = isPublicApp
      ? extractPublicAppNameFromUrl(githubRepoUrl)
      : extractAppNameFromUrl(githubRepoUrl);
    if (!appName) {
      return null;
    }

    let namespace: string;
    let serviceAccountName: string;
    let envLabel: string;

    if (isPublicApp) {
      namespace = `pub-${appName}-ns`;
      serviceAccountName = `pub-${appName}-sa`;
      envLabel = 'Production';
    } else {
      // IDP-983: Use staging namespace when staging is enabled
      namespace = enableStagingEnvironment
        ? `qp-${appName}-staging-ns`
        : `qp-${appName}-ns`;
      serviceAccountName = `qp-${appName}-sa`;
      envLabel = enableStagingEnvironment ? 'Staging' : 'Production';
    }

    const principal = `${workloadIdentityPool}/subject/ns/${namespace}/sa/${serviceAccountName}`;

    return { serviceAccountName, namespace, appName, principal, envLabel };
  }, [
    githubRepoUrl,
    isPublicApp,
    enableStagingEnvironment,
    workloadIdentityPool,
  ]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!serviceAccountInfo) {
    return (
      <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
        Enter a GitHub repository URL above to see the Workload Identity
        principal.
      </Alert>
    );
  }

  return (
    <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        GCP Workload Identity Principal ({serviceAccountInfo.envLabel})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: 1,
            p: 1,
          }}
        >
          <Typography
            variant="body2"
            component="code"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              wordBreak: 'break-all',
              flex: 1,
            }}
          >
            {serviceAccountInfo.principal}
          </Typography>
          <Tooltip title={copied ? 'Copied!' : 'Copy principal'}>
            <IconButton
              size="small"
              onClick={() => handleCopy(serviceAccountInfo.principal)}
              sx={{ p: 0.5, flexShrink: 0 }}
            >
              <ContentCopyIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
        {enableStagingEnvironment && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 500,
              color: 'warning.dark',
              backgroundColor: 'warning.lighter',
              p: 1,
              borderRadius: 1,
            }}
          >
            <strong>Note:</strong> You are deploying to staging. Grant this{' '}
            <strong>staging WIF principal</strong> the required IAM roles to
            access GCP resources (Secret Manager, Cloud Storage, etc.). After
            promoting to production, you'll need to grant the production WIF
            principal separately.
          </Typography>
        )}
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          Use this principal in your Terraform script to grant GCP resource
          access (e.g., Secret Manager, Cloud SQL, BigQuery). See the{' '}
          <a
            href="https://skystage.wellsky.cloud/catalog/default/component/skystage/docs/deploy-internal-apps-user-guide/#workload-identity-federation-wif"
            target="_blank"
            rel="noopener noreferrer"
          >
            WIF Setup Guide
          </a>{' '}
          for details.
        </Typography>
      </Box>
    </Alert>
  );
};
